import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { FUNDING_SOURCES } from '../utils/loyalty';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const EDITABLE_FIELDS = [
    {
        key: 'earnPercentage',
        label: 'On-base earn percentage',
        unit: 'fraction of eligible amount',
        max: 1,
        help: 'On base (Commissary and Exchange). A fraction, not a whole-number percent — enter 0.05 for 5%, not 5. Converted to points at the earn rate below.',
    },
    {
        key: 'offBaseEarnPercentage',
        label: 'Off-base earn percentage',
        unit: 'fraction of eligible amount',
        max: 1,
        help: 'For receipts the reviewer marks off base. Same format: enter 0.01 for 1%. Off-base points count toward the same annual cap.',
    },
    {
        key: 'earnRatePointsPerDollar',
        label: 'Earn rate',
        unit: 'points per $1',
        help: 'Receipts earn the percentage above of the eligible amount, converted to points at this rate.',
    },
    {
        key: 'annualCapPoints',
        label: 'Annual cap (points)',
        unit: 'points',
        help: (
            <>
                $120/yr at the redemption rate: <strong>1,440 at 12 points = $1</strong>, 1,320 at
                the old 11 points = $1. This is the value sent to squad-rewards and the cap actually
                enforced. <strong>Change it to 1,440 only after squad-rewards is live at 12:1</strong>{' '}
                — 1,440 at 11:1 would pay out $130.91/yr, not $120.
            </>
        ),
    },
    {
        key: 'annualCapDollars',
        label: 'Annual cap ($)',
        unit: '$ / year',
        help: 'Confirmed — used in current marketing copy.',
    },
    {
        key: 'welcomeBonusPoints',
        label: 'Welcome bonus',
        unit: 'points',
        help: null,
    },
];

const REFERENCE_ROWS = [
    {
        label: 'Redemption rate',
        value: '12 points = $1 (~8.3¢ cash-out value per point) from the earning-first launch; 11 points = $1 before',
    },
    { label: 'Vesting — monthly member', value: '1/12 of the eligible amount released each month' },
    { label: 'Vesting — annual member', value: 'Releases in full on day 1' },
    { label: 'Vesting — locked account', value: 'Accrues, but nothing vests (no cash-out) until the account is unlocked' },
];

const ALL_FUNDING_VALUES = FUNDING_SOURCES.map((f) => f.value);
const fundingLabel = (value) => FUNDING_SOURCES.find((f) => f.value === value)?.label || value;
const sameSet = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

function LoyaltyRules() {
    const [config, setConfig] = useState(null);
    const [drafts, setDrafts] = useState({});
    // null = untouched (mirror config); an array once the admin toggles a box.
    const [fundingDraft, setFundingDraft] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allowed, setAllowed] = useState(null); // null = still checking

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        (async () => {
            // This is a CircleOne-only, money-affecting screen — mirrors the sidebar's
            // showSettings gate (role === 'superadmin' || (role === 'admin' && source
            // !== 'mml')). The real authorization boundary is server-side
            // (requireNonMmlAdmin on /admin/loyalty/config); this only prevents an
            // MML-sourced admin from landing on a form that would 403 on save.
            const currentUser = await authManager.validateUser().catch(() => null);
            const isMmlAdmin = currentUser?.role === 'admin' && currentUser?.source === 'mml';
            if (isMmlAdmin) {
                setAllowed(false);
                setLoading(false);
                return;
            }
            setAllowed(true);

            try {
                const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/config`);
                const data = await res.json();
                if (data.status === 'success') {
                    setConfig(data.data);
                } else {
                    toast.error(data.message || 'Failed to load loyalty config.');
                }
            } catch (err) {
                console.error('Error fetching loyalty config:', err);
                toast.error('Error loading loyalty config.');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleSave = async (field) => {
        const fieldKey = field.key;
        const draft = drafts[fieldKey];
        if (draft === undefined || draft === '') return;
        const value = Number(draft);
        if (!Number.isFinite(value) || value <= 0) {
            toast.error('Enter a positive number.');
            return;
        }
        // Fractions (the earn percentages) can't exceed 1 — same bound the API
        // enforces; catches 5 typed for 5% before the confirm dialog.
        if (field.max !== undefined && value > field.max) {
            toast.error(`${field.label} is a fraction: enter 0.05 for 5%, not 5.`);
            return;
        }

        const previous = config?.[fieldKey];
        // Live, no-deploy-needed, money-affecting config — a plain click-to-save
        // invites fat-fingering, so require an explicit confirm of old -> new.
        const confirmed = window.confirm(
            `Change "${field.label}" from ${previous} to ${value} ${field.unit}?\n\nThis takes effect immediately for all members.`,
        );
        if (!confirmed) return;

        try {
            setSaving(true);
            const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ [fieldKey]: value }),
            });
            const data = await res.json();
            if (data.status === 'success') {
                setConfig(data.data);
                setDrafts((d) => {
                    const next = { ...d };
                    delete next[fieldKey];
                    return next;
                });
                toast.success('Saved.');
            } else {
                toast.error(data.message || 'Save failed.');
            }
        } catch (err) {
            console.error('Error saving loyalty config:', err);
            toast.error('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const configFunding = config?.enabledFundingSources ?? ALL_FUNDING_VALUES;
    const fundingSelection = fundingDraft ?? configFunding;
    const fundingDirty = fundingDraft !== null && !sameSet(fundingDraft, configFunding);

    const toggleFunding = (value) => {
        setFundingDraft((prev) => {
            const base = prev ?? configFunding;
            return base.includes(value)
                ? base.filter((v) => v !== value)
                : ALL_FUNDING_VALUES.filter((v) => base.includes(v) || v === value);
        });
    };

    const handleSaveFundingSources = async () => {
        if (fundingSelection.length === 0) {
            toast.error('At least one funding source has to stay enabled.');
            return;
        }
        const confirmed = window.confirm(
            `Set the funding sources a reviewer can pick when approving a receipt to:\n\n`
            + `${fundingSelection.map(fundingLabel).join('\n')}\n\nThis takes effect immediately.`,
        );
        if (!confirmed) return;

        try {
            setSaving(true);
            const res = await authManager.authenticatedFetch(`${Base_Url}/admin/loyalty/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabledFundingSources: fundingSelection }),
            });
            const data = await res.json();
            if (data.status === 'success') {
                setConfig(data.data);
                setFundingDraft(null);
                toast.success('Saved.');
            } else {
                toast.error(data.message || 'Save failed.');
            }
        } catch (err) {
            console.error('Error saving funding sources:', err);
            toast.error('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    if (allowed === false) {
        return (
            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body text-center py-5">
                            <h5>Not available for this admin account.</h5>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Loyalty Program Rules</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <h5 className="mb-1">Earn-side rules</h5>
                            <p className="text-muted mb-3">
                                Applied by this app&apos;s own loyalty earn flow and Premium-signup bonus grant —
                                editable here directly. Changes take effect immediately, no deploy needed.
                            </p>
                            {loading ? (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table width="100%" className="table table-striped table-bordered txt_lft">
                                        <tbody>
                                            {EDITABLE_FIELDS.map((f) => (
                                                <tr key={f.key}>
                                                    <td style={{ width: '220px' }}>
                                                        <strong>{f.label}</strong>
                                                    </td>
                                                    <td style={{ width: '320px' }}>
                                                        <div className="input-group">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="any"
                                                                className="form-control"
                                                                // A longer unit label (e.g. "fraction of eligible
                                                                // amount" vs "points") shouldn't be able to squeeze
                                                                // the number input down to an unreadable sliver —
                                                                // this floor keeps it usable regardless of how long
                                                                // a future field's unit text ends up being.
                                                                style={{ minWidth: '90px' }}
                                                                value={drafts[f.key] ?? config?.[f.key] ?? ''}
                                                                onChange={(e) =>
                                                                    setDrafts((d) => ({ ...d, [f.key]: e.target.value }))
                                                                }
                                                            />
                                                            <div className="input-group-append">
                                                                <span className="input-group-text f_sz13">{f.unit}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {f.help && <div className="text-muted mb-2">{f.help}</div>}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-primary"
                                                            disabled={saving || drafts[f.key] === undefined}
                                                            onClick={() => handleSave(f)}
                                                        >
                                                            Save
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body">
                            <h5 className="mb-1">Funding sources</h5>
                            <p className="text-muted mb-3">
                                Which funding sources a reviewer can pick when approving a receipt. The set of
                                possible sources is fixed (each is a contract with Squad Rewards&apos; cap
                                accounting) — this only controls which are <em>offered</em>, e.g. hiding
                                &quot;Buy 5 Get 50&quot; while that promo isn&apos;t running. At least one must
                                stay enabled.
                            </p>
                            {loading ? (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {FUNDING_SOURCES.map((f) => (
                                        <div className="form-check" key={f.value}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`funding-${f.value}`}
                                                checked={fundingSelection.includes(f.value)}
                                                onChange={() => toggleFunding(f.value)}
                                            />
                                            <label className="form-check-label" htmlFor={`funding-${f.value}`}>
                                                {f.label}
                                            </label>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary mt-3"
                                        disabled={saving || !fundingDirty || fundingSelection.length === 0}
                                        onClick={handleSaveFundingSources}
                                    >
                                        Save
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-body">
                            <h5 className="mb-1">Redemption &amp; vesting (reference only)</h5>
                            <p className="text-muted mb-3">
                                Enforced in Squad Rewards, not this app — shown here for reference only, sourced
                                from <code>somo-circle1-api/docs/LOYALTY_CENTER_PLAN.md</code>. Editable config for
                                vesting is blocked on{' '}
                                <a
                                    href="https://github.com/EphronTech/circle1-squad-rewards/pull/32"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    circle1-squad-rewards#32
                                </a>.
                            </p>
                            <div className="table-responsive">
                                <table width="100%" className="table table-striped table-bordered txt_lft">
                                    <tbody>
                                        {REFERENCE_ROWS.map((r) => (
                                            <tr key={r.label}>
                                                <td style={{ width: '220px' }}>
                                                    <span className='fwt_600' >{r.label}</span>
                                                </td>
                                                <td>{r.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LoyaltyRules;
